<template>
  <main class="min-h-screen bg-theme-bg">
    <!-- Loading skeleton -->
    <div v-if="loading" class="max-w-7xl mx-auto px-4 py-8">
      <div class="grid grid-cols-1 lg:grid-cols-5 gap-8">
        <div class="lg:col-span-3 space-y-4">
          <div class="aspect-[4/3] bg-theme-bg2 rounded-2xl animate-pulse"></div>
          <div class="flex gap-2">
            <div v-for="i in 4" :key="i" class="w-20 h-20 bg-theme-bg2 rounded-xl animate-pulse"></div>
          </div>
        </div>
        <div class="lg:col-span-2 space-y-4">
          <div class="h-8 bg-theme-bg2 rounded-lg animate-pulse w-3/4"></div>
          <div class="h-6 bg-theme-bg2 rounded-lg animate-pulse w-1/2"></div>
          <div class="h-48 bg-theme-bg2 rounded-2xl animate-pulse"></div>
        </div>
      </div>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="max-w-7xl mx-auto px-4 py-16 text-center">
      <div class="bg-theme-card rounded-2xl p-8 max-w-md mx-auto shadow-lg border border-theme-border">
        <i class="pi pi-exclamation-triangle text-4xl text-red-500 mb-4"></i>
        <h2 class="text-xl font-bold text-theme-text mb-2">Something went wrong</h2>
        <p class="text-theme-muted mb-4">{{ error }}</p>
        <button @click="fetchEquipment" class="px-6 py-2.5 bg-primary-600 hover:bg-primary-500 text-white rounded-xl font-semibold transition">
          Try Again
        </button>
      </div>
    </div>

    <!-- Main content -->
    <div v-else-if="equipment" class="max-w-7xl mx-auto px-4 py-4 sm:py-6">
      <!-- Breadcrumb -->
      <nav class="flex items-center text-sm text-theme-muted mb-4 gap-1.5">
        <router-link to="/" class="hover:text-primary-600 transition">Home</router-link>
        <i class="pi pi-angle-right text-xs"></i>
        <router-link to="/browse" class="hover:text-primary-600 transition">Browse</router-link>
        <i class="pi pi-angle-right text-xs"></i>
        <span class="text-theme-text font-medium truncate max-w-[200px]">{{ equipment.name }}</span>
      </nav>

      <!-- Two-column layout -->
      <div class="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
        <!-- LEFT: Gallery + Tabs -->
        <div class="lg:col-span-3 space-y-6">
          <EquipmentImage :equipment="equipment" />
          <EquipmentTabs :equipment="equipment" />
        </div>

        <!-- RIGHT: Booking sidebar (sticky) -->
        <div class="lg:col-span-2">
          <div class="lg:sticky lg:top-20">
            <EquipmentDetailsPanel :equipment="equipment" :loading="loading" :error="error" />
          </div>
        </div>
      </div>

      <!-- Related items -->
      <div class="mt-10">
        <RelatedItems />
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import EquipmentImage from '@/components/EquipmentImage.vue';
import EquipmentDetailsPanel from '@/components/EquipmentDetails.vue';
import EquipmentTabs from '@/components/EquipmentTabs.vue';
import RelatedItems from '@/components/RelatedItems.vue';
import { useEquipmentsStore } from '@/store/equipments';

const store = useEquipmentsStore();
const loading = ref(true);
const route = useRoute();

const equipment = computed(() => store.selectedEquipment);
const error = computed(() => store.error);

const fetchEquipment = async () => {
  loading.value = true;
  await store.getEquipmentById(route.params.id);
  loading.value = false;
};

onMounted(fetchEquipment);
</script>

<style scoped>
/* Additional styles can go here if needed */
</style>
